import React, { FC, useEffect, useMemo, useState } from 'react';
import { html } from 'lit/static-html.js';
import { ColumnDef } from '@tanstack/react-table';
import {
  DataTable,
  Grid,
  GridCell,
  IconButton,
  Input,
  PageSection,
  Spacer,
  Stack,
  TextTitle,
  Tooltip,
} from '@pypestream/design-system';
import { TranslationComponent, useTranslation } from '@pypestream/translations';

import {
  useLoadAsDraftModal,
  useRepublishModal,
  VersionCard,
} from '../components';
import { StudioPageProps, useSolutionNameValidation } from '../hooks';
import { WithLoadingState } from '../router';
import { capitalizeWords, formatDateTime } from '../utils';
import {
  sendProStudioEvent,
  useProStudioCtxSelector,
} from '../xstate/app.xstate';
import { SolutionVersion } from '../xstate/prostudio.api-service.types';

import './versions.scss';

type VersionsPageProps = StudioPageProps;

const filterUniqueVersions = (data: SolutionVersion[]): SolutionVersion[] => {
  const versionMap = new Map<string, SolutionVersion>();

  data.forEach((item) => {
    const key = `${item.env}-${item.version}`;
    const existing = versionMap.get(key);

    if (!existing || new Date(item.time) > new Date(existing.time)) {
      versionMap.set(key, item);
    }
  });

  return Array.from(versionMap.values());
};

const mergeVersions = (data: SolutionVersion[]): SolutionVersion[] => {
  const versionMap = new Map<string, SolutionVersion & { env: string }>();

  data.forEach((item) => {
    const key = JSON.stringify({
      picture: item.picture,
      bot: item.bot,
      customer: item.customer,
      email: item.email,
      first_name: item.first_name,
      last_name: item.last_name,
      uuid: item.uuid,
      version: item.version,
    });

    const existing = versionMap.get(key);

    if (existing) {
      const envSet = new Set(
        existing.env.split(', ').concat(item.env.toLowerCase())
      );
      existing.env = Array.from(envSet).sort().join(', ');
      if (new Date(item.time) > new Date(existing.time)) {
        existing.time = item.time;
      }
    } else {
      versionMap.set(key, { ...item, env: item.env.toLowerCase() });
    }
  });

  return Array.from(versionMap.values());
};

const VersionsPage: FC<VersionsPageProps> = () => {
  const [botVersion, setBotVersion] = useState<SolutionVersion | undefined>(
    undefined
  );

  const [isEditMode, setIsEditMode] = useState(false);

  const {
    displayName,
    botVersions,
    deployedVersions,
    draftVersion,
    envs,
    botSolutionId,
    solutionId,
  } = useProStudioCtxSelector((ctx) => ({
    botSolutionId: ctx.selectedBot?.id,
    solutionId: ctx.selectedBot?.uniqueId,
    displayName: ctx.selectedBot?.displayName,
    botVersions: filterUniqueVersions(ctx.versions || []),
    deployedVersions: ctx.deployedVersions,
    draftVersion: ctx.draftVersion,
    customerId: ctx.customerId,
    projectId: ctx.project?.projectProStudioSetting?.id,
    botId:
      ctx.customerId && ctx.botName
        ? `${ctx.customerId}.${ctx.botName}`
        : undefined,
    envs: ['draft', ...(ctx.envs ? [...ctx.envs].reverse() : [])],
  }));

  const [solutionName, setSolutionName] = useState<string>(displayName || '');

  useEffect(() => {
    if (displayName && !solutionName) {
      setSolutionName(displayName);
    }
  }, [displayName, solutionName]);

  const memoizedBotVersions = useMemo(() => botVersions, [botVersions]);

  const {
    errorMessage,
    isSafetySolutionName,
    isValidName,
    getTag,
    transformAndValidateSolutionName,
  } = useSolutionNameValidation(solutionName);

  const getDeployedVersions = () => {
    const versions = [
      ...Object.entries(deployedVersions || {}),
      ['draft', draftVersion],
    ] as [string, string][];

    for (const env of envs) {
      if (!versions.some(([key]) => key === env)) {
        versions.push([env, '']);
      }
    }

    versions.sort((a, b) => envs.indexOf(a[0]) - envs.indexOf(b[0]));

    return versions;
  };

  const deployedVersionsAsArray = getDeployedVersions();

  const [t] = useTranslation();
  const { republishModal, openModal: openRepublishModal } = useRepublishModal({
    botVersion: botVersion,
  });
  const { loadAsDraftModal, openModal: openLoadAsDraftModal } =
    useLoadAsDraftModal();

  const columns: ColumnDef<SolutionVersion>[] = [
    {
      accessorKey: 'version',
      accessorFn: (row) => row.version,
      header: () =>
        t('pro-studio/versions:table.column1', {
          defaultValue: 'Version',
        }),
      cell: ({ row }) =>
        html`<ps-text-subtitle size="small">
          ${row?.original?.version}
        </ps-text-subtitle>`,
    },
    {
      accessorKey: 'env',
      accessorFn: (row) => row.env,
      header: () =>
        t('pro-studio/versions:table.column2', {
          defaultValue: 'Environment',
        }),
      cell: ({ row }) => {
        if (!row || !row?.original) {
          return null;
        }

        const isActive = deployedVersionsAsArray
          .map((ver) => ({
            version: ver[1],
            env: ver[0].toLowerCase(),
          }))
          .some((ver) => {
            const environments = row.original.env.toLowerCase().split(', ');
            return (
              ver.version === row.original.version &&
              environments.includes(ver.env)
            );
          });

        const { env } = row.original;
        return html`<ps-text-subtitle
          size="small"
          variant=${isActive ? 'primary' : 'tertiary'}
        >
          <!-- ${t(`pro-studio/common:environments.${env.toLowerCase()}`, {
            defaultValue: env,
          })} -->
          ${capitalizeWords(env)}
        </ps-text-subtitle>`;
      },
    },
    {
      accessorKey: 'time',
      accessorFn: (row) => row.time,
      header: () =>
        t('pro-studio/versions:table.column3', {
          defaultValue: 'Last Published',
        }),
      cell: ({ row }) => {
        if (!row || !row?.original) {
          return null;
        }

        const { first_name, last_name, picture, time } = row.original;
        const userName = `${first_name} ${last_name}`;
        return html`<ps-stack alignItems="center" gutter="2xsmall" nowrap>
          ${userName.trim()
            ? html`<ps-avatar
                  size="small"
                  src=${picture}
                  label=${userName}
                ></ps-avatar>
                <ps-text-body truncate>${userName}</ps-text-body>
                <ps-text-body truncate
                  >at ${time ? formatDateTime(time) : ''}
                </ps-text-body>`
            : null}
        </ps-stack>`;
      },
    },
    {
      id: 'actions',
      header: () =>
        html`<ps-text-body style="opacity: 0">Actions</ps-text-body>`,
      cell: ({ row }) =>
        html`<ps-stack
          alignItems="center"
          justifyContent="end"
          gutter="xsmall"
          nowrap
        >
          <!-- <ps-button
            variant="secondary"
            size="small"
            @click=${openLoadAsDraftModal}
            ><ps-icon name="refresh" slot="prefix" size="xsmall"></ps-icon>${t(
            'pro-studio/versions:table.buttons.load_as_draft',
            {
              defaultValue: 'Load as draft',
            }
          )}</ps-button
          > -->
          <ps-button
            variant="secondary"
            size="small"
            @click=${() => {
              setBotVersion(row?.original);
              openRepublishModal();
            }}
            ><ps-icon name="deploy" slot="prefix" size="xsmall"></ps-icon>
            ${t('pro-studio/versions:table.buttons.republish', {
              defaultValue: 'Republish',
            })}
          </ps-button>
          <ps-icon-button
            name="download"
            size="small"
            @click=${() => {
              sendProStudioEvent({
                type: 'getVersionFile',
                version: row?.original.version,
              });
            }}
          ></ps-icon-button>
        </ps-stack>`,
      meta: {
        align: 'end',
      },
    },
  ];

  const copyDuploCloudID = (id: string) => {
    sendProStudioEvent({
      type: 'copyToClipboard',
      value: id,
      message:
        t('pro-studio/versions:duplo_cloud_id.message', {
          defaultValue: 'DuploCloud ID copied to clipboard',
        }) || '',
    });
  };

  const onSaveSolutionName = () => {
    if (!solutionName.trim() || !botSolutionId || !solutionId) {
      return;
    }

    setIsEditMode(false);

    if (solutionName.trim() === displayName) {
      return;
    }

    sendProStudioEvent({
      type: 'updateSolutionName',
      name: solutionName,
      solutionId,
    });
  };

  return (
    <React.Fragment>
      <Stack gutter="xsmall">
        {isEditMode ? (
          <Stack direction="column" gutter="xsmall">
            <Input
              test-id="ps-versions-page-solution-name-input"
              value={solutionName}
              name="solution-name"
              // @ts-expect-error - TODO: onInput event type has no value property
              onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                setSolutionName(
                  transformAndValidateSolutionName(e.target.value)
                );
              }}
              size="small"
              className="c-versions-page-title-input"
              hasError={isSafetySolutionName}
              helpText={errorMessage}
            />
            <Stack gutter="small">
              {getTag(
                t('pro-studio/homepage:modal.form.name.validation.uniqueness', {
                  defaultValue: 'Uniqueness',
                }),
                'passedUniquenessCheck'
              )}
              {getTag(
                t('pro-studio/homepage:modal.form.name.validation.safety', {
                  defaultValue: 'Safety',
                }),
                'passedNameSafetyCheck'
              )}
            </Stack>
          </Stack>
        ) : (
          <TextTitle test-id="ps-versions-page-title" size="small">
            {displayName}
          </TextTitle>
        )}
        <div className="c-versions-page-title-controls">
          <Stack gutter="xsmall" alignItems="center">
            {isEditMode ? (
              <>
                <IconButton
                  test-id="ps-versions-page-confirm-new-solution-name-button"
                  onClick={onSaveSolutionName}
                  name="check"
                  size="small"
                  disabled={!isValidName}
                />
                <IconButton
                  test-id="ps-versions-page-cancel-edit-solution-name-button"
                  onClick={() => {
                    setIsEditMode(false);
                    setSolutionName(displayName || '');
                  }}
                  name="close"
                  size="small"
                />
              </>
            ) : (
              <>
                <IconButton
                  test-id="ps-versions-page-edit-solution-name-button"
                  onClick={() => {
                    setIsEditMode(true);
                  }}
                  name="pencil"
                  size="small"
                  disabled={false}
                />
                <Tooltip
                  test-id="ps-versions-page-duplo-cloud-id-copy-tooltip"
                  content={
                    t('pro-studio/versions:duplo_cloud_id.copy', {
                      defaultValue: 'Copy DuploCloud ID',
                    }) || ''
                  }
                >
                  <IconButton
                    disabled={!botSolutionId}
                    test-id="ps-versions-page-duplo-cloud-id-copy-button"
                    onClick={() => {
                      if (!botSolutionId) {
                        return;
                      }

                      copyDuploCloudID(botSolutionId);
                    }}
                    name="copy"
                    size="small"
                  />
                </Tooltip>
              </>
            )}
          </Stack>
        </div>
        {/** <TextCaption test-id="ps-versions-page-org-name">
          for {orgName}
        </TextCaption>
        **/}
      </Stack>
      <Spacer size="large" />
      <TextTitle size="xsmall" test-id="ps-versions-page-cards-title">
        <TranslationComponent i18nKey="pro-studio/versions:cards.title">
          Environments
        </TranslationComponent>
      </TextTitle>
      <Spacer />
      <Grid gutter="large" rowGutter="large" alignItems="stretch">
        {deployedVersionsAsArray.map(([env, version]) => {
          const blankSolution = {
            version,
            env,
            time: '',
            first_name: '',
            last_name: '',
            picture: '',
            uuid: '',
            bot: '',
            customer: '',
            email: '',
          };

          const solution =
            env === 'draft' || !version
              ? blankSolution
              : memoizedBotVersions?.find(
                  (item) => item.version === version && item.env === env
                ) || blankSolution;

          return (
            <GridCell key={env} xsmall="12" medium="6" large="4">
              <VersionCard key={env} solution={solution} />
            </GridCell>
          );
        })}
      </Grid>
      <Spacer size="large" />
      <TextTitle size="xsmall" test-id="ps-versions-page-table-title">
        <TranslationComponent i18nKey="pro-studio/versions:table.title">
          Publishing versions
        </TranslationComponent>
      </TextTitle>
      <Spacer />
      <PageSection size="small">
        <DataTable
          test-id="ps-versions-page-versions-table"
          size="small"
          columns={columns}
          actionable={false}
          data={mergeVersions(memoizedBotVersions).map((ver) => ({
            ...ver,
            id: ver.version,
            name: ver.version,
          }))}
        />
      </PageSection>

      {loadAsDraftModal}
      {republishModal}
    </React.Fragment>
  );
};

export const Versions = WithLoadingState(VersionsPage);
